import React from 'react';
import { Helmet } from 'react-helmet';
import './LegalPage.css';

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Terms of Service & Privacy Policy - Predica.AI</title>
        <meta name="description" content="Predica.AI Terms of Service and Privacy Policy" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8 prose prose-sm sm:prose-base">
        <h1 className="text-3xl font-bold mb-8 text-gray-900">
          Predica.AI - Terms of Service and Privacy Policy
        </h1>
        
        <div className="legal-content text-gray-900">
          <p className="text-sm mb-4">Last updated and effective date: 27 January 2025</p>
          
          <p className="mb-6">
            PLEASE READ THESE TERMS OF SERVICE ("AGREEMENT" OR "TERMS OF SERVICE") CAREFULLY BEFORE USING THE SERVICES OFFERED BY PREDICA.AI. THIS AGREEMENT SETS FORTH THE LEGALLY BINDING TERMS AND CONDITIONS FOR YOUR USE OF THE PREDICA.AI WEBSITE AND ALL RELATED SERVICES, INCLUDING, WITHOUT LIMITATION, ANY FEATURES, CONTENT, WEBSITES (INCLUDING PREDICA.AI) OR APPLICATIONS OFFERED FROM TIME TO TIME BY PREDICA IN CONNECTION THEREWITH (COLLECTIVELY "SERVICE(S)"). BY USING THE SERVICES IN ANY MANNER, YOU AGREE TO BE BOUND BY THIS AGREEMENT.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Acceptance of Terms of Service</h2>
          <p className="mb-6">
            The Service is offered subject to acceptance without modification of all of these Terms of Service and all other operating rules, policies and procedures that may be published from time to time in connection with the Services by Predica.AI. In addition, some services offered through the Service may be subject to additional terms and conditions promulgated by Predica.AI from time to time; your use of such services is subject to those additional terms and conditions, which are incorporated into these Terms of Service by this reference.
          </p>
          
          <p className="mb-6">
            Predica.AI may, in its sole discretion, refuse to offer the Service to any person or entity and change its eligibility criteria at any time. This provision is void where prohibited by law and the right to access the Service is revoked in such jurisdictions.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Rules and Conduct</h2>
          <p className="mb-6">
            By using Predica.AI, you agree that the Service is intended solely for the purpose of analyzing and interacting with documents and data through AI-powered features. You acknowledge and agree that when uploading documents, you must have the legal right to use and process those documents.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">User Responsibilities and Prohibited Uses</h2>
          <h3 className="text-xl font-bold mt-4 mb-2">Document Upload Responsibilities</h3>
          <p className="mb-6">
            When uploading documents, you must:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Have legal authority to process and analyze the content</li>
            <li>Ensure you have obtained necessary consents if documents contain:
              <ul className="list-disc pl-8">
                <li>Personal information of third parties</li>
                <li>Confidential business information</li>
                <li>Licensed or protected content</li>
              </ul>
            </li>
            <li>Verify documents do not contain:
              <ul className="list-disc pl-8">
                <li>Classified or restricted information</li>
                <li>Protected health information (PHI)</li>
                <li>Payment card data (PCI)</li>
                <li>Other regulated data types without proper authorization</li>
              </ul>
            </li>
          </ul>
          
          <h3 className="text-xl font-bold mt-4 mb-2">Prohibited Content</h3>
          <p className="mb-6">
            You agree not to upload or process documents containing:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Illegal or unlawful content</li>
            <li>Malicious software or code</li>
            <li>Discriminatory or hateful material</li>
            <li>Harassing or threatening content</li>
            <li>Deliberately false or misleading information</li>
            <li>Content that infringes intellectual property rights</li>
            <li>Sexually explicit material</li>
            <li>Content promoting violence or terrorism</li>
          </ul>
          
          <h3 className="text-xl font-bold mt-4 mb-2">Prohibited Activities</h3>
          <p className="mb-6">
            You shall not use the Service to:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Impersonate others or provide false information</li>
            <li>Conduct illegal or fraudulent activities</li>
            <li>Attempt to reverse engineer the Service or AI models</li>
            <li>Interfere with other users' access to the Service</li>
            <li>Generate or distribute spam or malware</li>
            <li>Conduct automated scanning or testing without authorization</li>
            <li>Exceed usage limits or circumvent restrictions</li>
            <li>Resell or redistribute the Service or AI outputs</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Account Security</h2>
          <p className="mb-6">
            You are responsible for:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Maintaining the confidentiality of your credentials</li>
            <li>All activities occurring under your account</li>
            <li>Immediately notifying us of any unauthorized access</li>
            <li>Ensuring secure access to your documents and data</li>
            <li>Properly logging out from shared devices</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Compliance Requirements</h2>
          <p className="mb-6">
            You must comply with:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>All applicable laws and regulations</li>
            <li>Industry-specific requirements</li>
            <li>Data protection and privacy laws</li>
            <li>Export control regulations</li>
            <li>Intellectual property rights</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Detailed Compliance Obligations</h2>
          <h3 className="text-xl font-bold mt-4 mb-2">Data Protection Compliance</h3>
          <p className="mb-6">
            1. General Data Protection:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Process personal data lawfully and transparently</li>
            <li>Collect data only for specified, legitimate purposes</li>
            <li>Ensure data accuracy and keep it up to date</li>
            <li>Store data only for as long as necessary</li>
            <li>Maintain confidentiality and security</li>
          </ul>
          
          <p className="mb-6">
            2. Business Data Handling:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Respect confidentiality agreements</li>
            <li>Protect trade secrets and proprietary information</li>
            <li>Handle competitive information ethically</li>
            <li>Maintain data segregation where required</li>
          </ul>
          
          <p className="mb-6">
            3. Document Retention:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Follow industry-specific retention requirements</li>
            <li>Implement appropriate deletion procedures</li>
            <li>Maintain audit trails of document handling</li>
            <li>Comply with legal hold obligations</li>
          </ul>
          
          <h3 className="text-xl font-bold mt-4 mb-2">Export Control Compliance</h3>
          <p className="mb-6">
            1. You must comply with:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>International trade regulations</li>
            <li>Economic sanctions programs</li>
            <li>Export control classifications</li>
            <li>Technology transfer restrictions</li>
          </ul>
          
          <p className="mb-6">
            2. You shall not use the Service if you are:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Located in a sanctioned country</li>
            <li>Subject to export restrictions</li>
            <li>Prohibited from receiving AI technology</li>
          </ul>
          
          <h3 className="text-xl font-bold mt-4 mb-2">Industry-Specific Requirements</h3>
          <p className="mb-6">
            1. Financial Services:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Comply with financial regulations</li>
            <li>Maintain transaction records</li>
            <li>Follow reporting requirements</li>
          </ul>
          
          <p className="mb-6">
            2. Healthcare:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Protect patient privacy</li>
            <li>Secure medical information</li>
            <li>Follow healthcare regulations</li>
          </ul>
          
          <p className="mb-6">
            3. Legal Services:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Maintain client confidentiality</li>
            <li>Protect privileged information</li>
            <li>Follow legal ethics rules</li>
          </ul>
          
          <h3 className="text-xl font-bold mt-4 mb-2">Intellectual Property Compliance</h3>
          <p className="mb-6">
            1. Document Usage:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Respect copyright protections</li>
            <li>Honor trademark rights</li>
            <li>Protect patent information</li>
          </ul>
          
          <p className="mb-6">
            2. Content Licensing:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Obtain necessary licenses</li>
            <li>Respect usage restrictions</li>
            <li>Maintain license records</li>
          </ul>
          
          <h3 className="text-xl font-bold mt-4 mb-2">Regulatory Reporting</h3>
          <p className="mb-6">
            1. You must:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Report security incidents</li>
            <li>Notify of data breaches</li>
            <li>Cooperate with audits</li>
            <li>Maintain compliance records</li>
          </ul>
          
          <p className="mb-6">
            2. Documentation:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Keep records of compliance activities</li>
            <li>Maintain audit trails</li>
            <li>Document incident responses</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Consequences of Non-Compliance</h2>
          <p className="mb-6">
            1. Service Impact:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Immediate service suspension</li>
            <li>Account termination</li>
            <li>Data access restrictions</li>
          </ul>
          
          <p className="mb-6">
            2. Legal Consequences:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Regulatory penalties</li>
            <li>Legal proceedings</li>
            <li>Damage claims</li>
          </ul>
          
          <p className="mb-6">
            3. Remediation:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Compliance verification</li>
            <li>Corrective actions</li>
            <li>Additional monitoring</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Consequences of Violation</h2>
          <p className="mb-6">
            Violation of these responsibilities may result in:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Immediate suspension or termination of your account</li>
            <li>Removal of offending content</li>
            <li>Reporting to relevant authorities</li>
            <li>Legal action if necessary</li>
            <li>Forfeiture of any unused subscription period</li>
          </ul>
          
          <p className="mb-6">
            By using Predica.AI, you confirm that you are over 18 years old (or the legal age in your country) and are not using the service for any illegal or unauthorized purpose.
          </p>
          
          <p className="mb-6">
            Further, you shall not (directly or indirectly): (i) take any action that imposes or may impose an unreasonable or disproportionately large load on Predica.AI's infrastructure; (ii) interfere or attempt to interfere with the proper working of the Service; (iii) bypass any measures Predica.AI may use to prevent or restrict access to the Service; (iv) use manual or automated software, devices, or other processes to "crawl," "scrape," or "spider" any page of the Site; (v) decompile, reverse engineer, or otherwise attempt to obtain the source code of the Service; and (vi) reproduce, duplicate, copy, sell, resell or exploit any portion of the Service without express written permission.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Document Processing and AI Analysis</h2>
          <p className="mb-6">
            Predica.AI processes your documents using artificial intelligence to provide analysis, insights, and interactive features. While we strive for accuracy, you acknowledge that AI-generated responses and analysis may not be perfect and should be verified for critical use cases. You retain ownership of your documents and the content you upload.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">AI Limitations and Disclaimers</h2>
          <p className="mb-6">
            1. No Professional Advice: The Service does not provide professional, legal, financial, medical or other regulated advice. Any AI-generated analysis or insights are for informational purposes only and should not be relied upon as professional advice.
          </p>
          
          <p className="mb-6">
            2. User Responsibility: You are solely responsible for:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Verifying the accuracy of AI-generated outputs</li>
            <li>Determining the appropriateness of using the Service for your specific needs</li>
            <li>Ensuring compliance with any regulations in your field</li>
            <li>Making independent professional judgments based on your expertise</li>
          </ul>
          
          <p className="mb-6">
            3. Service Reliability:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We strive to maintain high availability but cannot guarantee uninterrupted access</li>
            <li>AI models and features may be updated or modified without notice</li>
            <li>Some features may be in beta testing and marked as such</li>
            <li>Performance may vary based on document complexity and system load</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">DMCA and Copyright Policy</h2>
          <p className="mb-6">
            We respect intellectual property rights and expect our users to do the same. If you believe your copyright is being infringed through our Service, please contact us at legal@predica.ai with details of the alleged infringement.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Subscription Terms and Payment</h2>
          <p className="mb-6">
            You agree that Predica.AI provides immediate access to digital content upon purchase. The subscription will automatically renew for the same period unless cancelled before the renewal date. Due to the nature of our AI processing services, we cannot offer refunds for used services or past subscription periods.
          </p>
          
          <p className="mb-6">
            Predica.AI offers various subscription tiers with different features and limitations. You can cancel your subscription at any time through your account settings. Changes to pricing or features will be communicated in advance and will take effect at your next renewal date.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Subscription Cancellation</h2>
          <p className="mb-6">
            1. You may cancel your subscription at any time through your account settings
          </p>
          <p className="mb-6">
            2. Cancellation will take effect at the end of your current billing period
          </p>
          <p className="mb-6">
            3. No partial refunds will be provided for unused portions of the subscription period
          </p>
          <p className="mb-6">
            4. You will continue to have access to the Service until the end of your current billing period
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Data Privacy and Security</h2>
          <p className="mb-6">
            We take data privacy seriously. Documents you upload are processed securely and are not shared with third parties except as required to provide the Service. We implement industry-standard security measures to protect your data.
          </p>
          
          <p className="mb-6">
            We collect and process personal information as necessary to provide the Service, including:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Account information (email, name, profile details)</li>
            <li>Usage data and analytics</li>
            <li>Document metadata and content for processing</li>
            <li>Basic subscription status information (payment processing is handled by Stripe)</li>
          </ul>
          
          <p className="mb-6">
            Your data is processed in accordance with applicable data protection laws. For EU users, we comply with GDPR requirements.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Security Measures</h2>
          <p className="mb-6">
            We implement comprehensive security controls including:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Encryption:
              <ul className="list-disc pl-8">
                <li>- AES-256 encryption for data at rest</li>
                <li>- TLS 1.2+ encryption for data in transit</li>
              </ul>
            </li>
            <li>Access Controls:
              <ul className="list-disc pl-8">
                <li>- Regular access reviews</li>
              </ul>
            </li>
            <li>Infrastructure Security:
              <ul className="list-disc pl-8">
                <li>- Regular security assessments</li>
                <li>- Vulnerability scanning</li>
                <li>- Security patch management</li>
              </ul>
            </li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Data Breach Notification</h2>
          <p className="mb-6">
            In the event of a data breach that affects your personal information:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We will notify affected users without undue delay</li>
            <li>Notification will include:
              <ul className="list-disc pl-8">
                <li>- Nature of the breach</li>
                <li>- Likely consequences</li>
                <li>- Measures taken or proposed</li>
                <li>- Contact point for more information</li>
              </ul>
            </li>
            <li>We will cooperate with authorities as required by applicable laws</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Limitation of Liability</h2>
          <p className="mb-6">
            IN NO EVENT SHALL PREDICA.AI OR ITS DIRECTORS, EMPLOYEES, AGENTS, PARTNERS, SUPPLIERS OR CONTENT PROVIDERS, BE LIABLE UNDER CONTRACT, TORT, STRICT LIABILITY, NEGLIGENCE OR ANY OTHER LEGAL OR EQUITABLE THEORY WITH RESPECT TO THE SERVICE (I) FOR ANY LOST PROFITS, DATA LOSS, COST OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES, OR SPECIAL, INDIRECT, INCIDENTAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES OF ANY KIND WHATSOEVER, OR SUBSTITUTE GOODS OR SERVICES, (II) FOR YOUR RELIANCE ON THE SERVICE OR AI-GENERATED CONTENT (III) FOR ANY DIRECT DAMAGES IN EXCESS (IN THE AGGREGATE) OF THE FEES PAID BY YOU FOR THE SERVICE OR, IF GREATER, $500. SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATIONS AND EXCLUSIONS MAY NOT APPLY TO YOU.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Disclaimer</h2>
          <p className="mb-6">
            THE SERVICE (INCLUDING, WITHOUT LIMITATION, ANY CONTENT, AI-GENERATED ANALYSIS, OR DOCUMENT PROCESSING) IS PROVIDED "AS IS" AND "AS AVAILABLE" AND IS WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF TITLE, NON-INFRINGEMENT, MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE, AND ANY WARRANTIES IMPLIED BY ANY COURSE OF PERFORMANCE OR USAGE OF TRADE, ALL OF WHICH ARE EXPRESSLY DISCLAIMED. PREDICA.AI AND ITS DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, PARTNERS AND CONTENT PROVIDERS DO NOT WARRANT THAT: (A) THE SERVICE WILL BE SECURE OR AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; (B) ANY DEFECTS OR ERRORS WILL BE CORRECTED; (C) ANY CONTENT OR SOFTWARE AVAILABLE AT OR THROUGH THE SERVICE IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS; (D) THE RESULTS OF USING THE SERVICE WILL MEET YOUR REQUIREMENTS OR BE ACCURATE, RELIABLE, OR ERROR-FREE. YOUR USE OF THE SERVICE IS SOLELY AT YOUR OWN RISK.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Data Processing and Storage</h2>
          <p className="mb-6">
            By using our Service, you acknowledge and agree that:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Your documents and data may be processed by AI models and stored on secure servers</li>
            <li>We maintain appropriate technical and organizational measures to protect your data</li>
            <li>Document analysis and AI responses are generated automatically and may require human verification</li>
            <li>We retain your data only as long as necessary to provide the Service or as required by law</li>
            <li>You can request deletion of your data at any time through your account settings</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Intellectual Property Rights</h2>
          <p className="mb-6">
            1. The Service and its original content (excluding user-uploaded content), features, and functionality are owned by Predica.AI and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
          </p>
          <p className="mb-6">
            2. You retain all rights to your documents and content uploaded to the Service
          </p>
          <p className="mb-6">
            3. You grant Predica.AI a limited license to process, analyze, and store your content as necessary to provide the Service
          </p>
          <p className="mb-6">
            4. AI-generated analysis and insights are provided for your use but do not transfer any intellectual property rights to you
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">User Representations</h2>
          <p className="mb-6">
            By using the Service, you represent and warrant that:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>You have the legal right to upload and process all documents submitted to the Service</li>
            <li>You will not use the Service for any illegal or unauthorized purpose</li>
            <li>You will not violate any applicable laws or regulations</li>
            <li>You will not infringe upon any third-party intellectual property rights</li>
            <li>You will maintain the confidentiality of your account credentials</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Termination</h2>
          <p className="mb-6">
            1. You may terminate your account at any time through the account settings
          </p>
          <p className="mb-6">
            2. We may terminate or suspend your account immediately for any violation of these Terms
          </p>
          <p className="mb-6">
            3. Upon termination:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>- Your access to the Service will cease immediately</li>
            <li>- We will retain your data for a reasonable period as required by law</li>
            <li>- You remain liable for all fees incurred before termination</li>
            <li>- Sections on liability, privacy, and intellectual property survive termination</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Changes to Service</h2>
          <p className="mb-6">
            Predica.AI reserves the right to:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Modify or discontinue any portion of the Service</li>
            <li>Change subscription fees upon reasonable notice</li>
            <li>Update these Terms of Service</li>
            <li>Limit access to certain features based on subscription level</li>
            <li>Add, remove, or modify AI analysis capabilities</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Governing Law and Jurisdiction</h2>
          <p className="mb-6">
            These Terms shall be governed by and construed in accordance with the laws of Singapore, without regard to its conflict of law provisions. You agree to submit to the exclusive jurisdiction of the courts located in Singapore for resolution of any dispute arising from these Terms or your use of the Service.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Contact Information</h2>
          <p className="mb-6">
            For any questions about these Terms or the Service:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Email: legal@predica.ai</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4">Privacy Policy</h2>
          <p className="mb-6">
            Information Collection and Use
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We collect several types of information to provide and improve our Service:</li>
            <li>1. Personal Information:
              <ul className="list-disc pl-8">
                <li>- Email address and profile information from Google Sign-In</li>
                <li>- Usage data and interaction with the Service</li>
                <li>- Payment and billing information</li>
                <li>- Communication records when you contact us</li>
              </ul>
            </li>
            <li>2. Document Information:
              <ul className="list-disc pl-8">
                <li>- Content of uploaded documents</li>
                <li>- Document metadata (file names, creation dates, etc.)</li>
                <li>- Generated analysis and chat history</li>
                <li>- Processing status and results</li>
              </ul>
            </li>
            <li>3. Technical Information:
              <ul className="list-disc pl-8">
                <li>- IP addresses and device information</li>
                <li>- Browser type and version</li>
                <li>- Usage patterns and preferences</li>
                <li>- Performance data and error logs</li>
              </ul>
            </li>
          </ul>
          
          <p className="mb-6">
            Use of Information
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We use collected information for:</li>
            <li>1. Providing and maintaining the Service</li>
            <li>2. Processing and analyzing your documents</li>
            <li>3. Improving our AI models and service quality</li>
            <li>4. Managing your account and subscription</li>
            <li>5. Communicating with you about the Service</li>
            <li>6. Ensuring compliance with these Terms</li>
          </ul>
          
          <p className="mb-6">
            Data Retention
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>1. Account Information: Retained while your account is active</li>
            <li>2. Documents: Stored until deleted by you or account termination</li>
            <li>3. Chat History: Maintained for service improvement and your reference</li>
            <li>4. Payment Information: Payment processing and storage is handled by Stripe, our payment processor. We only store basic subscription status information</li>
            <li>5. Specific Retention Periods:
              <ul className="list-disc pl-8">
                <li>- Inactive accounts: Deleted after 1 month of inactivity</li>
                <li>- Chat history: Retained for 1 month</li>
                <li>- Subscription records: Retained for tax compliance purposes</li>
                <li>- System logs: Retained for 90 days</li>
              </ul>
            </li>
            <li>6. Data Deletion Timeline:
              <ul className="list-disc pl-8">
                <li>- User-requested deletions: Processed within 30 days</li>
                <li>- Account deletion: All associated data removed within 30 days</li>
                <li>- Backup retention: Backups containing deleted data are purged within 90 days</li>
              </ul>
            </li>
          </ul>
          
          <p className="mb-6">
            Data Security
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We implement and maintain appropriate technical and organizational security measures:</li>
            <li>1. Encryption of data in transit and at rest</li>
            <li>2. Regular security assessments and updates</li>
            <li>3. Access controls and authentication</li>
            <li>4. Secure data processing environments</li>
            <li>5. Regular backup procedures</li>
          </ul>
          
          <p className="mb-6">
            Third-Party Services
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Our Service integrates with:</li>
            <li>1. Google for authentication</li>
            <li>2. Stripe for payment processing</li>
            <li>These third parties have access to your information only to perform specific tasks and are obligated to protect your data.</li>
          </ul>
          
          <p className="mb-6">
            Your Rights
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>You have the right to:</li>
            <li>1. Access your personal information</li>
            <li>2. Correct inaccurate data</li>
            <li>3. Request deletion of your data</li>
            <li>4. Export your information</li>
            <li>5. Withdraw consent for processing</li>
          </ul>
          
          <p className="mb-6">
            Cookie Policy
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We use cookies and similar tracking technologies to:</li>
            <li>1. Maintain your session and preferences</li>
            <li>2. Analyze usage patterns</li>
            <li>3. Improve service performance</li>
            <li>4. Provide security features</li>
          </ul>
          
          <p className="mb-6">
            Children's Privacy
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Our Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children.</li>
          </ul>
          
          <p className="mb-6">
            International Data Transfers
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.</li>
          </ul>
          
          <p className="mb-6">
            Changes to Privacy Policy
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</li>
          </ul>
          
          <p className="mb-6">
            Consent
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>By using our Service, you consent to our Privacy Policy and agree to its terms. You may withdraw your consent at any time by discontinuing use of the Service.</li>
          </ul>
          
          <p className="mb-6">
            Other Information
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>We retain personal data for as long as necessary to provide the services and fulfill the transactions you have requested, comply with our legal obligations, resolve disputes, enforce our agreements, and other legitimate and lawful business purposes. Because these needs can vary for different data types in the context of different services, actual retention periods can vary significantly based on criteria such as user expectations or consent, the sensitivity of the data, the availability of automated controls that enable users to delete data, and our legal or contractual obligations.</li>
          </ul>
          
          <p className="mb-6">
            As part of our normal operations, your information may be stored in computers in other countries outside of your home country. By giving us information, you consent to this kind of information transfer. Irrespective of where your information resides, we'll comply with applicable law and abide by our commitments herein.
          </p>
          
          <p className="mb-6">
            We don't want your personal information if you're under 18. Do not provide it to us. If your child is under 13 and you believe your child has provided us with their personal information, please contact us to have such information removed.
          </p>
          
          <p className="mb-6">
            Version History
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>- January 27, 2025: Updated terms to include detailed compliance obligations and data protection measures</li>
            <li>- Previous versions of this agreement can be requested by contacting legal@predica.ai</li>
          </ul>
          
          <p className="mb-6">
            European Economic Area, United Kingdom, Swiss and California users
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>The following rights are granted under the European General Data Protection Regulation ("GDPR") and California Consumer Privacy Act ("CCPA"). Predica AI applies these rights to all users of our products, regardless of your location:</li>
            <li>The right to know what personal information is collected.</li>
            <li>The right to know if personal information is being shared, and to whom.</li>
            <li>The right to access your personal information.</li>
            <li>The right to exercise your privacy rights without being discriminated against.</li>
          </ul>
          
          <p className="mb-6">
            EEA, UK, and Swiss Users: Our lawful bases for collecting and processing personal information under the GDPR include:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>Performing our contract with you and providing our services.</li>
            <li>Legitimate interests: we receive technical and interaction data of users, which may include IP addresses, to improve the security and reliability of our services and prevent abuse, and to understand where people learn about Predica.AI.</li>
            <li>Consent: where we ask for your consent to process your information, you can always withdraw this consent.</li>
          </ul>
          
          <p className="mb-6">
            Under the GDPR, EEA, UK, and Swiss users have additional rights:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>The right to request correction or erasure of personal information.</li>
            <li>The right to object to processing your personal information.</li>
            <li>The right to right to transfer or receive a copy of the personal information in a usable and portable format, when any automated processing of personal data is based on your consent or a contract with you.</li>
            <li>The rights to withdraw your consent to processing, when the processing is based on your consent.</li>
            <li>When we are processing data on behalf of another party that is the "data controller," you should direct your request to that party. You also have the right to lodge a complaint with a supervisory authority, but we encourage you to first contact us with any questions or concerns.</li>
          </ul>
          
          <p className="mb-6">
            California Users: Under the CCPA, California residents have additional rights:
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>The right to request personal information about to be deleted, subject to several exceptions.</li>
            <li>The right to opt-out of the sale of personal information. Note that we do not "sell" personal information as defined by the CCPA and have not done so in the past 12 months. You may designate, in writing or through a power of attorney, an authorized agent to make requests on your behalf to exercise your rights under the CCPA. Before accepting such a request from an agent, we will require the agent to provide proof you have authorized it to act on your behalf, and we may need you to verify your identity directly with us. Further, to provide or delete specific pieces of personal information we will need to verify your identity to the degree of certainty required by law. We will verify your request by asking you to send it from the email address associated with your account or requiring you to provide information necessary to verify your account.</li>
          </ul>
          
          <p className="mb-6">
            Indemnification
          </p>
          <ul className="list-disc pl-8 mb-6">
            <li>You agree to indemnify, defend, and hold harmless Predica.AI, its officers, directors, employees, agents, and licensors from and against all losses, 
expenses, damages, and costs, including reasonable attorneys' fees, resulting from:</li>
            <li>1. Your violation of these Terms</li>
            <li>2. Your use or misuse of the Service</li>
            <li>3. Your violation of any third-party rights, including intellectual property rights</li>
            <li>4. Any content you submit, post, or transmit through the Service</li>
            <li>5. Any activity related to your account, whether by you or by any other person accessing the Service using your account</li>
          </ul>
          
          <p className="mb-6">
            This indemnification obligation will survive the termination or expiration of your use of the Service.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPage; 